import { useCallback, useEffect, useRef, useState } from 'react';
import GameSocket from '../net/GameSocket';
import { createBrawlEngine } from '../engine/interpolation';
import { worldOf } from '../engine/constants';

/**
 * One real-time game session (PvP queue+match or a Brawler run) over GameSocket.
 * Snapshots go straight into the engine (no React state per tick); React state only holds
 * the lifecycle: status, queue, match, panel, end, error.
 *
 * start(onOpen)  creates a fresh socket, connects, and calls onOpen(socket) on the first open
 *                (send queue.join / pve.join there)
 * stop()         closes the socket
 */
export default function useBrawlSession({ speed, createSocket } = {}) {
  const engineRef = useRef(null);
  if (!engineRef.current) engineRef.current = createBrawlEngine({ speed: speed || 260 });
  const engine = engineRef.current;
  const socketRef = useRef(null);
  const endTimer = useRef(null);
  const createRef = useRef(createSocket);
  createRef.current = createSocket;

  const [status, setStatus] = useState('idle');
  const [queue, setQueue] = useState(null);
  const [room, setRoom] = useState(null); // «play with a friend»: {code, stake, expiresInSec}
  const [match, setMatch] = useState(null);
  const [panel, setPanel] = useState(null);
  const [end, setEnd] = useState(null);
  const [error, setError] = useState(null);
  const [rtt, setRtt] = useState(null);

  useEffect(() => {
    if (speed) engine.predictor.setSpeed(speed);
  }, [speed, engine]);

  const stop = useCallback(() => {
    clearTimeout(endTimer.current);
    const s = socketRef.current;
    socketRef.current = null;
    if (s) s.close();
  }, []);

  const start = useCallback(async (onOpen) => {
    stop();
    engine.reset();
    setQueue(null);
    setRoom(null);
    setMatch(null);
    setPanel(null);
    setEnd(null);
    setError(null);
    const sock = createRef.current ? createRef.current() : new GameSocket();
    socketRef.current = sock;
    const mine = (fn) => (payload) => { if (socketRef.current === sock) fn(payload); };

    sock.on('status', mine(setStatus));
    sock.on('open', mine(({ reconnect } = {}) => {
      if (!reconnect && onOpen) onOpen(sock);
    }));
    sock.on('rtt', mine(({ rtt: r }) => {
      engine.setRtt(r);
      setRtt(Math.round(r));
    }));
    sock.on('queue.status', mine(setQueue));
    sock.on('room.created', mine(setRoom));
    sock.on('match', mine((msg) => {
      engine.reset();
      engine.setYou(msg.you);
      engine.setWorld(worldOf(msg));
      engine.setFighters(msg.fighters);
      setMatch({ ...msg, receivedAt: Date.now() });
      if (msg.panel) setPanel({ ...msg.panel, at: Date.now() });
      setEnd(null);
    }));
    sock.on('s', mine((msg) => engine.push(msg, Date.now())));
    sock.on('panel', mine((msg) => {
      engine.predictor.reset(null);
      if (msg.fighters) engine.setFighters(msg.fighters);
      // a new panel brings new enemies (kind / maxHp) and barrels
      setMatch((m) => (m ? {
        ...m, fighters: msg.fighters || m.fighters, barrels: msg.barrels || m.barrels, panel: { n: msg.n, total: msg.total },
      } : m));
      setPanel({ n: msg.n, total: msg.total, at: Date.now() });
    }));
    sock.on('end', mine((msg) => {
      setEnd({ ...msg, at: Date.now() });
      // the session is over — no reconnect attempts after this
      clearTimeout(endTimer.current);
      endTimer.current = setTimeout(() => { if (socketRef.current === sock) stop(); }, 1500);
    }));
    sock.on('error', mine((msg) => setError({ ...msg, at: Date.now() })));
    await sock.connect();
  }, [engine, stop]);

  useEffect(() => () => stop(), [stop]);

  const sendInput = useCallback((input) => {
    engine.setInput(input);
    socketRef.current?.sendInput(input);
  }, [engine]);

  /** 'atk' | 'cast' | 'jump' | 'drop' — jump/drop are predicted locally right away. */
  const sendAct = useCallback((a) => {
    if (a === 'jump') engine.jump();
    else if (a === 'drop') engine.drop();
    socketRef.current?.sendAct(a);
  }, [engine]);

  const send = useCallback((msg) => socketRef.current?.send(msg), []);
  const socket = useCallback(() => socketRef.current, []);

  return {
    engine, status, queue, room, match, panel, end, error, rtt,
    start, stop, sendInput, sendAct, send, socket, clearError: () => setError(null),
  };
}
